import {
  Button,
  Filter,
  Input,
  Plus,
  Search,
  SortAsc,
  useDebounce,
  User,
} from "@ews-admin/global-design-system";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Select } from "../../components/atoms/Select";
import { AddManagerModal } from "../../components/molecules/AddManagerModal/AddManagerModal";
import { AssignFacilityModal } from "../../components/molecules/AssignFacilityModal/AssignFacilityModal";
import { ConfirmationModal } from "../../components/molecules/ConfirmationModal/ConfirmationModal";
import { EditManagerModal } from "../../components/molecules/EditManagerModal/EditManagerModal";
import { ManagerCard } from "../../components/molecules/ManagerCard/ManagerCard";
import { Pagination } from "../../components/molecules/Pagination";
import managerApi from "../../services/manager.api";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  clearError,
  fetchManagersPageRequest,
  reloadManagersPage,
  selectManagersPageContent,
  selectManagersPageError,
  selectManagersPageLoading,
  selectManagersPageTotalElements,
  selectManagersPageTotalPages,
} from "../../store/reducers/managersSlice";
import { Manager, ManagerPageQuery } from "../../types/manager.definition";

/** MED-1469: the assignment filter of the list. */
type AssignmentFilter = "" | "assigned" | "unassigned";

/** MED-1635: managers per page of the server-paged list. */
const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

export const ManagerManagement: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Redux state — MED-1635: one server-side page, not the whole list
  const managers = useAppSelector(selectManagersPageContent);
  const totalElements = useAppSelector(selectManagersPageTotalElements);
  const totalPages = useAppSelector(selectManagersPageTotalPages);
  const isLoading = useAppSelector(selectManagersPageLoading);
  const loadError = useAppSelector(selectManagersPageError);

  // Local state
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  const [selectedManager, setSelectedManager] = useState<Manager | null>(null);
  const [isDisabling, setIsDisabling] = useState(false);
  const [isUnassigning, setIsUnassigning] = useState(false);

  // MED-1469: the "Filtrer" and "Trier" buttons drove nothing at all. Same shape as
  // the Établissements page: a toggled filter panel plus a sort toggle on the name.
  const [showFilters, setShowFilters] = useState(false);
  const [assignmentFilter, setAssignmentFilter] =
    useState<AssignmentFilter>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // MED-1635: search, filter and sort run on the server (GET /managers/page); the
  // API used to answer its default 20 rows, so managers after the 20th never showed.
  // `page` is 0-based; every search/filter/sort change goes back to the first page.
  const [page, setPage] = useState(0);
  const debouncedSearch = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);
  const [appliedSearch, setAppliedSearch] = useState("");

  useEffect(() => {
    setAppliedSearch(debouncedSearch.trim());
    setPage(0);
  }, [debouncedSearch]);

  const query = useMemo<ManagerPageQuery>(
    () => ({
      page,
      size: PAGE_SIZE,
      sort: `user.lastName,${sortOrder}`,
      ...(appliedSearch ? { search: appliedSearch } : {}),
      ...(assignmentFilter
        ? { assigned: assignmentFilter === "assigned" }
        : {}),
    }),
    [page, sortOrder, appliedSearch, assignmentFilter]
  );

  useEffect(() => {
    dispatch(fetchManagersPageRequest(query));
  }, [dispatch, query]);

  // A disable on the last row of the last page leaves that page empty: step back.
  useEffect(() => {
    if (!isLoading && page > 0 && totalPages > 0 && page >= totalPages) {
      setPage(totalPages - 1);
    }
  }, [isLoading, page, totalPages]);

  const reloadPage = () => {
    dispatch(reloadManagersPage());
  };

  // MED-1470: a real DELETE /managers/{id}. The list used to drop the row from Redux
  // without calling anything, so a reload brought the manager straight back.
  // MED-1515: that endpoint DISABLES the account — manager accounts are
  // FK-referenced by appointments and are never erased — so the control is
  // named after its effect, not after the HTTP verb behind it.
  const handleDisableConfirm = async () => {
    if (!selectedManager) return;

    setIsDisabling(true);
    try {
      await managerApi.disableManager(selectedManager.id);
      reloadPage();
      toast.success(t("admin.managers.toast.disableSuccess"));
      setShowDisableModal(false);
      setSelectedManager(null);
    } catch {
      toast.error(t("admin.managers.toast.disableError"));
    } finally {
      setIsDisabling(false);
    }
  };

  const handleDisableCancel = () => {
    setShowDisableModal(false);
    setSelectedManager(null);
  };

  const handleDisable = (manager: Manager) => {
    setSelectedManager(manager);
    setShowDisableModal(true);
  };

  const handleEdit = (manager: Manager) => {
    setSelectedManager(manager);
    setShowEditModal(true);
  };

  const handleAssignFacility = (manager: Manager) => {
    setSelectedManager(manager);
    setShowAssignModal(true);
  };

  const handleManagerClick = (manager: Manager) => {
    navigate(`/admin/managers/${manager.id}`);
  };

  const handleUnassign = (manager: Manager) => {
    setSelectedManager(manager);
    setShowUnassignModal(true);
  };

  // MED-1470: a real DELETE /managers/{id}/facility; the account stays enabled.
  const handleUnassignConfirm = async () => {
    if (!selectedManager) return;

    setIsUnassigning(true);
    try {
      await managerApi.unassignFacility(selectedManager.id);
      reloadPage();
      toast.success(t("admin.managers.toast.unassignSuccess"));
      setShowUnassignModal(false);
      setSelectedManager(null);
    } catch {
      toast.error(t("admin.managers.toast.unassignError"));
    } finally {
      setIsUnassigning(false);
    }
  };

  const handleUnassignCancel = () => {
    setShowUnassignModal(false);
    setSelectedManager(null);
  };

  const handleMutationSuccess = () => {
    reloadPage();
  };

  // Clear error when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex justify-between items-center page-header">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t("admin.managers.title")}
          </h2>
          <p className="text-gray-600">{t("admin.managers.subtitle")}</p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span>{t("admin.managers.addNew")}</span>
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="page-section">
        <div className="flex flex-col gap-4 sm:flex-row">
          <div className="flex-1">
            <Input
              placeholder={t("admin.managers.searchPlaceholder")}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              aria-label={t("admin.managers.searchPlaceholder")}
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center space-x-2 ${showFilters ? "bg-primary-50 border-primary-300" : ""}`}
            >
              <Filter className="w-4 h-4" />
              <span>{t("common.filter")}</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setSortOrder(sortOrder === "asc" ? "desc" : "asc");
                setPage(0);
              }}
              aria-label={t("admin.managers.sortByName")}
              className="flex items-center space-x-2"
            >
              <SortAsc className="w-4 h-4" />
              <span>{t("common.sort")}</span>
            </Button>
          </div>
        </div>

        {showFilters && (
          <div className="p-4 mt-4 rounded-lg border bg-neutral-50 border-neutral-200">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <Select
                  label={t("admin.managers.filters.assignment")}
                  value={assignmentFilter}
                  onChange={(value: unknown) => {
                    setAssignmentFilter(value as AssignmentFilter);
                    setPage(0);
                  }}
                  options={[
                    { label: t("common.all"), value: "" },
                    {
                      label: t("admin.managers.filters.assigned"),
                      value: "assigned",
                    },
                    {
                      label: t("admin.managers.filters.unassigned"),
                      value: "unassigned",
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Managers Grid */}
      <div className="page-section">
        <p
          className="mb-4 text-sm text-neutral-600"
          data-testid="managers-total"
        >
          {t("admin.managers.total", { count: totalElements })}
        </p>
        {loadError ? (
          <p className="py-12 text-center text-sm text-red-600" role="alert">
            {t("admin.managers.loadError")}
          </p>
        ) : managers.length === 0 ? (
          <div className="py-12 text-center">
            <User className="mx-auto w-12 h-12 text-neutral-400" />
            <h3 className="mt-2 text-sm font-medium text-neutral-900">
              {t("admin.managers.noManagers")}
            </h3>
            <p className="mt-1 text-sm text-neutral-500">
              {t("admin.managers.noManagersDescription")}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {managers.map(manager => (
              <ManagerCard
                key={manager.id}
                manager={manager}
                onClick={handleManagerClick}
                onAssignFacility={handleAssignFacility}
                onUnassignFacility={handleUnassign}
                onEdit={handleEdit}
                onDisable={handleDisable}
              />
            ))}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="page-section">
          <Pagination
            currentPage={page + 1}
            totalPages={totalPages}
            totalItems={totalElements}
            itemsPerPage={PAGE_SIZE}
            onPageChange={next => setPage(next - 1)}
            showPageNumbers={true}
            maxVisiblePages={5}
            showItemsPerPageSelector={false}
          />
        </div>
      )}

      <AddManagerModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      />

      {/* Edit Manager Modal — the real form, not a placeholder with a dead Save */}
      {showEditModal && selectedManager && (
        <EditManagerModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          manager={selectedManager}
          onSuccess={() => {
            setShowEditModal(false);
            handleMutationSuccess();
          }}
        />
      )}

      {/* Assign Facility Modal */}
      {showAssignModal && selectedManager && (
        <AssignFacilityModal
          isOpen={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          manager={selectedManager}
          onSuccess={() => {
            toast.success(t("admin.managers.toast.assignSuccess"));
            handleMutationSuccess();
          }}
        />
      )}

      {/* Disable Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDisableModal}
        onClose={handleDisableCancel}
        onConfirm={handleDisableConfirm}
        title={t("common.confirmations.disableManager")}
        message={t("common.confirmations.disableManagerMessage")}
        confirmLabel={t("common.disable")}
        cancelLabel={t("common.cancel")}
        variant="danger"
        isLoading={isDisabling}
      />

      {/* Unassign Confirmation Modal */}
      <ConfirmationModal
        isOpen={showUnassignModal}
        onClose={handleUnassignCancel}
        onConfirm={handleUnassignConfirm}
        title={t("common.confirmations.unassignManager")}
        message={t("common.confirmations.unassignManagerMessage")}
        confirmLabel={t("common.unassign")}
        cancelLabel={t("common.cancel")}
        variant="warning"
        isLoading={isUnassigning}
      />
    </div>
  );
};
